import { useState, useEffect, useCallback, useMemo } from 'react';
import * as api from './api';
import {
  Product,
  PermissionLevel,
  User,
  UserGroup,
  UserWithPermissions,
  Group,
  transformToLegacyUser,
  transformToLegacyGroup,
} from './types';

// =============================================================================
// Products Hook
// =============================================================================

interface UseProductsResult {
  products: Product[];
  productNames: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProducts(): UseProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getProducts();
      setProducts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const productNames = products.map(p => p.product_name);

  return { products, productNames, loading, error, refetch: fetchProducts };
}

// =============================================================================
// Permission Levels Hook
// =============================================================================

interface UsePermissionLevelsResult {
  levels: PermissionLevel[];
  levelNames: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePermissionLevels(): UsePermissionLevelsResult {
  const [levels, setLevels] = useState<PermissionLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLevels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getPermissionLevels();
      setLevels(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch permission levels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLevels();
  }, [fetchLevels]);

  const levelNames = levels.map(l => l.level_name);

  return { levels, levelNames, loading, error, refetch: fetchLevels };
}

// =============================================================================
// Users Hook with Pagination
// =============================================================================

interface UseUsersResult {
  users: User[];
  rawUsers: UserWithPermissions[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  totalItems: number;
  totalPages: number;
  setPage: (page: number) => void;
  setRowsPerPage: (limit: number) => void;
  refetch: () => Promise<void>;
}

export function useUsers(initialPage = 0, initialLimit = 25, searchTerm = ''): UseUsersResult {
  const [rawUsers, setRawUsers] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(initialPage);
  const [rowsPerPage, setRowsPerPageState] = useState(initialLimit);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API uses 1-based page, UI uses 0-based
      const response = await api.getUsersWithPermissions(page + 1, rowsPerPage, searchTerm);
      // Handle different response structures
      const data = response?.data || response || [];
      const pagination = response?.pagination || {};
      
      setRawUsers(Array.isArray(data) ? data : []);
      // API returns 'total' but interface expects 'totalItems', handle both
      const total = pagination.total ?? pagination.totalItems ?? data.length ?? 0;
      const totalPages = pagination.totalPages ?? Math.ceil(total / rowsPerPage);
      
      setTotalItems(total);
      setTotalPages(totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
      setRawUsers([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  // Reset to first page when search term changes
  useEffect(() => {
    setPageState(0);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setRowsPerPage = useCallback((newLimit: number) => {
    setRowsPerPageState(newLimit);
    setPageState(0); // Reset to first page when changing page size
  }, []);

  const users = useMemo(() => rawUsers.map(transformToLegacyUser), [rawUsers]);

  return { 
    users, 
    rawUsers, 
    loading, 
    error, 
    page,
    rowsPerPage,
    totalItems,
    totalPages,
    setPage, 
    setRowsPerPage,
    refetch: fetchUsers 
  };
}

// =============================================================================
// Groups Hook with Pagination
// =============================================================================

interface UseGroupsResult {
  groups: UserGroup[];
  rawGroups: Group[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  totalItems: number;
  totalPages: number;
  setPage: (page: number) => void;
  setRowsPerPage: (limit: number) => void;
  refetch: () => Promise<void>;
}

export function useGroups(initialPage = 0, initialLimit = 25): UseGroupsResult {
  const [rawGroups, setRawGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState(initialPage);
  const [rowsPerPage, setRowsPerPageState] = useState(initialLimit);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API uses 1-based page, UI uses 0-based
      const response = await api.getGroups(page + 1, rowsPerPage);
      // Handle different response structures
      const data = response?.data || response || [];
      const pagination = response?.pagination || {};
      
      setRawGroups(Array.isArray(data) ? data : []);
      // API returns 'total' but interface expects 'totalItems', handle both
      const total = pagination.total ?? pagination.totalItems ?? data.length ?? 0;
      const totalPages = pagination.totalPages ?? Math.ceil(total / rowsPerPage);
      
      setTotalItems(total);
      setTotalPages(totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch groups');
      setRawGroups([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setRowsPerPage = useCallback((newLimit: number) => {
    setRowsPerPageState(newLimit);
    setPageState(0); // Reset to first page when changing page size
  }, []);

  const groups = useMemo(() => rawGroups.map(transformToLegacyGroup), [rawGroups]);

  return { 
    groups, 
    rawGroups, 
    loading, 
    error, 
    page,
    rowsPerPage,
    totalItems,
    totalPages,
    setPage, 
    setRowsPerPage,
    refetch: fetchGroups 
  };
}

// =============================================================================
// Combined IAM Data Hook
// =============================================================================

interface UseIAMDataResult {
  // Users
  users: User[];
  usersLoading: boolean;
  usersPage: number;
  usersRowsPerPage: number;
  usersTotalItems: number;
  usersTotalPages: number;
  setUsersPage: (page: number) => void;
  setUsersRowsPerPage: (limit: number) => void;
  
  // Superadmin count (total across all users, for dashboard)
  superadminCount: number | undefined;
  superadminCountLoading: boolean;
  
  // Groups
  groups: UserGroup[];
  groupsLoading: boolean;
  groupsPage: number;
  groupsRowsPerPage: number;
  groupsTotalItems: number;
  groupsTotalPages: number;
  setGroupsPage: (page: number) => void;
  setGroupsRowsPerPage: (limit: number) => void;
  
  // Products & Levels
  products: Product[];
  productNames: string[];
  permissionLevels: PermissionLevel[];
  levelNames: string[];
  
  // General
  loading: boolean;
  error: string | null;
  refetchUsers: () => Promise<void>;
  refetchGroups: () => Promise<void>;
  refetchProducts: () => Promise<void>;
  refetchLevels: () => Promise<void>;
  refetchAll: () => Promise<void>;
}

export function useIAMData(userSearchTerm = ''): UseIAMDataResult {
  const { 
    users, 
    loading: usersLoading, 
    error: usersError, 
    page: usersPage,
    rowsPerPage: usersRowsPerPage,
    totalItems: usersTotalItems,
    totalPages: usersTotalPages,
    setPage: setUsersPage,
    setRowsPerPage: setUsersRowsPerPage,
    refetch: refetchUsers 
  } = useUsers(0, 25, userSearchTerm);
  
  const [superadminCount, setSuperadminCount] = useState<number | undefined>(undefined);
  const [superadminCountLoading, setSuperadminCountLoading] = useState(true);

  const fetchSuperadminCount = useCallback(async () => {
    setSuperadminCountLoading(true);
    try {
      const count = await api.getSuperadminCount();
      setSuperadminCount(count);
    } catch {
      setSuperadminCount(undefined);
    } finally {
      setSuperadminCountLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuperadminCount();
  }, [fetchSuperadminCount]);
  
  const { 
    groups, 
    loading: groupsLoading, 
    error: groupsError, 
    page: groupsPage,
    rowsPerPage: groupsRowsPerPage,
    totalItems: groupsTotalItems,
    totalPages: groupsTotalPages,
    setPage: setGroupsPage,
    setRowsPerPage: setGroupsRowsPerPage,
    refetch: refetchGroups 
  } = useGroups();
  
  const { 
    products, 
    productNames, 
    loading: productsLoading, 
    error: productsError, 
    refetch: refetchProducts 
  } = useProducts();
  
  const { 
    levels: permissionLevels, 
    levelNames, 
    loading: levelsLoading, 
    error: levelsError, 
    refetch: refetchLevels 
  } = usePermissionLevels();

  const loading = usersLoading || groupsLoading || productsLoading || levelsLoading;
  const error = usersError || groupsError || productsError || levelsError;

  const refetchAll = useCallback(async () => {
    await Promise.all([
      refetchUsers(),
      refetchGroups(),
      refetchProducts(),
      refetchLevels(),
      fetchSuperadminCount(),
    ]);
  }, [refetchUsers, refetchGroups, refetchProducts, refetchLevels, fetchSuperadminCount]);

  return {
    // Users
    users,
    usersLoading,
    usersPage,
    usersRowsPerPage,
    usersTotalItems,
    usersTotalPages,
    setUsersPage,
    setUsersRowsPerPage,
    
    // Superadmin count
    superadminCount,
    superadminCountLoading,
    
    // Groups
    groups,
    groupsLoading,
    groupsPage,
    groupsRowsPerPage,
    groupsTotalItems,
    groupsTotalPages,
    setGroupsPage,
    setGroupsRowsPerPage,
    
    // Products & Levels
    products,
    productNames,
    permissionLevels,
    levelNames,
    
    // General
    loading,
    error,
    refetchUsers,
    refetchGroups,
    refetchProducts,
    refetchLevels,
    refetchAll,
  };
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseMutationResult<T> {
  mutate: (...args: any[]) => Promise<T>;
  loading: boolean;
  error: string | null;
}

export function useSetUserPermissions(): UseMutationResult<void> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (
    userId: number,
    permissions: Array<{ productId: number; levelName: string }>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await api.setUserPermissions(userId, permissions);
    } catch (err: any) {
      setError(err.message || 'Failed to set user permissions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function useCreateGroup(): UseMutationResult<Group> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (groupName: string, description?: string) => {
    setLoading(true);
    setError(null);
    try {
      return await api.createGroup(groupName, description);
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function useDeleteGroup(): UseMutationResult<void> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (groupId: number) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteGroup(groupId);
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function useSetGroupPermissions(): UseMutationResult<void> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (
    groupId: number,
    permissions: Array<{ productId: number; levelName: string }>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await api.setGroupPermissions(groupId, permissions);
    } catch (err: any) {
      setError(err.message || 'Failed to set group permissions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function useAddUsersToGroup(): UseMutationResult<{ added: number; alreadyMembers: number }> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (groupId: number, userIds: number[]) => {
    setLoading(true);
    setError(null);
    try {
      return await api.addUsersToGroup(groupId, userIds);
    } catch (err: any) {
      setError(err.message || 'Failed to add users to group');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function useRemoveUsersFromGroup(): UseMutationResult<{ removed: number }> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (groupId: number, userIds: number[]) => {
    setLoading(true);
    setError(null);
    try {
      return await api.removeUsersFromGroup(groupId, userIds);
    } catch (err: any) {
      setError(err.message || 'Failed to remove users from group');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}

export function usePromoteToSuperadmin(): UseMutationResult<void> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      await api.promoteToSuperadmin(userId);
    } catch (err: any) {
      setError(err.message || 'Failed to promote user to superadmin');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, loading, error };
}
